import {FormEvent, ReactNode} from "react";
import {css, cx} from "@panda/css";

import {BrandBinocularsIcon} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";
import {useUserDevice} from "@pg-mono/user-device";

import {Modal} from "@/components/modal/Modal";
import {HOME_MATCH_PREFERENCES_WIZARD_CONTENT} from "@/features/home_match/constants/preferences/home_match_preferences_wizard_content";
import {zIndexes} from "@/features/km_app/constants/z_indexes";
import {useKeyboardFitModalContent} from "@/hooks/use_keyboard_fit_modal_content";

import {UserFlowModalStepContentTransition} from "./UserFlowModalStepContentTransition";

const {PROGRESS} = HOME_MATCH_PREFERENCES_WIZARD_CONTENT;

interface IProps {
    actionsAlign?: "center" | "end";
    children?: ReactNode;
    contentKey?: string;
    cover?: ReactNode;
    dataTestId?: string;
    description?: ReactNode;
    footer?: ReactNode;
    isOpen: boolean;
    onAfterClose?: () => void;
    onClose: () => void;
    onSubmit?: (event: FormEvent<HTMLFormElement>) => void;
    progress: number | null;
    progressTestId?: string;
    secondaryAction?: ReactNode;
    shouldCloseOnOverlayClick?: boolean;
    settle?: boolean;
    showIllustration?: boolean;
    title?: ReactNode;
    zIndex?: number;
}

export const UserFlowModal = (props: IProps) => {
    const {
        actionsAlign = "end",
        children,
        contentKey,
        cover,
        dataTestId,
        description,
        footer,
        isOpen,
        onAfterClose,
        onClose,
        onSubmit,
        progress,
        progressTestId,
        secondaryAction,
        settle = false,
        shouldCloseOnOverlayClick = false,
        showIllustration = true,
        title,
        zIndex = zIndexes.landingOverlay
    } = props;
    const {isMobile} = useUserDevice();
    const setFrameRef = useKeyboardFitModalContent(Boolean(isMobile && isOpen));
    const hasIntro = title != null || description != null;
    const introAlign = isMobile ? "left" : "center";
    const progressValue = progress == null ? null : Math.min(100, Math.max(0, progress));
    const stepBody = (
        <>
            {hasIntro && (
                <div className={introStyle}>
                    {title != null && (
                        <Text align={introAlign} as="h2" color="var(--colors-secondary)" preserveFontSize variant="headline_4">
                            {title}
                        </Text>
                    )}
                    {description != null && (
                        <Text align={introAlign} as="div" color="var(--colors-gray-800)" preserveFontSize variant="body_copy_2">
                            {description}
                        </Text>
                    )}
                </div>
            )}
            {children}
        </>
    );
    const frame = (
        <>
            <Modal.TopBar progress={progressValue} progressLabel={PROGRESS} progressTestId={progressTestId} />

            <div className={cx(contentStyle, !contentKey && hasIntro && contentColumnStyle)}>
                {contentKey ? (
                    <UserFlowModalStepContentTransition className={cx(hasIntro && contentColumnStyle)} contentKey={contentKey} settle={settle}>
                        {stepBody}
                    </UserFlowModalStepContentTransition>
                ) : (
                    stepBody
                )}
            </div>

            <div className={cx(footerStyle, actionsAlign === "center" && footerCenterStyle)}>
                {showIllustration && (
                    <span className={illustrationStyle} data-flow-illustration="">
                        <BrandBinocularsIcon height="14.9rem" width="10.3rem" />
                    </span>
                )}
                {(footer != null || secondaryAction != null) && (
                    <Modal.FloatingActions stacked={secondaryAction != null}>
                        {footer}
                        {secondaryAction != null && <div className={secondaryStartStyle}>{secondaryAction}</div>}
                    </Modal.FloatingActions>
                )}
            </div>
        </>
    );

    return (
        <Modal
            className={modalStyle}
            closeButtonClassName={closeButtonStyle}
            dataTestId={dataTestId}
            isOpen={isOpen}
            onAfterClose={onAfterClose}
            onModalClose={onClose}
            shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
            showCloseButton={!cover}
            zIndex={zIndex}
        >
            <Modal.Layout.Empty className={layoutStyle}>
                {onSubmit ? (
                    <form className={stepStyle} onSubmit={onSubmit} ref={setFrameRef}>
                        {frame}
                    </form>
                ) : (
                    <div className={stepStyle} ref={setFrameRef}>
                        {frame}
                    </div>
                )}
                {cover && <div className={coverStyle}>{cover}</div>}
            </Modal.Layout.Empty>
        </Modal>
    );
};

const closeButtonStyle = css({
    top: "2.6rem"
});

const modalStyle = css({
    borderRadius: 0,
    height: "100%",
    inset: 0,
    maxHeight: "100%",
    maxWidth: "100%",
    overflow: "hidden",
    width: "100%",

    md: {
        height: "auto",
        inset: "auto",
        maxHeight: "100vh",
        width: "80rem"
    }
});

const layoutStyle = css({
    backgroundColor: "var(--colors-white)",
    display: "flex",
    flexDirection: "column",
    position: "relative",

    "&&": {
        overflow: "hidden"
    },

    md: {
        backgroundColor: "var(--colors-gray-100)",

        "&&": {
            borderRadius: 2,
            overflow: "hidden"
        }
    }
});

const coverStyle = css({
    alignItems: "center",
    display: "flex",
    inset: 0,
    justifyContent: "center",
    position: "absolute",
    zIndex: "var(--zIndex-raised)"
});

const stepStyle = css({
    display: "flex",
    flexDirection: "column",
    height: "100%",
    maxHeight: "100%",
    minHeight: 0,

    "&:has([data-sms-code]:focus-within) [data-flow-illustration]": {
        transform: "scale(calc(61 / 115))",
        transformOrigin: "left bottom"
    },

    md: {
        height: "auto",
        maxHeight: "100vh",

        "&:has([data-sms-code]:focus-within) [data-flow-illustration]": {
            transform: "none"
        }
    }
});

const contentStyle = css({
    flex: "1 1 auto",
    minHeight: 0,
    overflow: "auto",
    pb: 2,
    pt: 2,
    px: 1.5,

    md: {
        pb: 7.5,
        pt: 3,
        px: 7.5
    }
});

const contentColumnStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 3
});

const introStyle = css({
    display: "flex",
    flexDirection: "column",
    flexShrink: 0,
    gap: 2
});

const footerStyle = css({
    alignItems: "stretch",
    display: "flex",
    flexDirection: "column",
    flexShrink: 0,
    gap: 2,
    height: "auto",
    justifyContent: "flex-end",
    overflow: "visible",
    position: "relative",

    md: {
        alignItems: "center",
        flexDirection: "row",
        height: "9.6rem",
        justifyContent: "flex-end",
        pb: 0,
        pl: 7.5,
        pr: 7.5
    }
});

const secondaryStartStyle = css({
    alignSelf: "center",
    position: "relative",
    zIndex: "var(--zIndex-base)",

    md: {
        alignSelf: "auto",
        mr: "auto",
        order: -1
    }
});

const footerCenterStyle = css({
    md: {
        justifyContent: "center",
        pl: 0,
        pr: 0
    }
});

const illustrationStyle = css({
    bottom: 7,
    left: 1,
    pointerEvents: "none",
    position: "absolute",
    zIndex: 0,

    md: {
        bottom: "-3rem",
        left: "16.2rem",
        pointerEvents: "auto",
        zIndex: "auto"
    }
});
import {FormEvent, ReactNode} from "react";
import {css, cx} from "@panda/css";

import {BrandBinocularsIcon} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";
import {useUserDevice} from "@pg-mono/user-device";

import {Modal} from "@/components/modal/Modal";
import {HOME_MATCH_PREFERENCES_WIZARD_CONTENT} from "@/features/home_match/constants/preferences/home_match_preferences_wizard_content";
import {zIndexes} from "@/features/km_app/constants/z_indexes";
import {useKeyboardFitModalContent} from "@/hooks/use_keyboard_fit_modal_content";

import {UserFlowModalStepContentTransition} from "./UserFlowModalStepContentTransition";

const {PROGRESS} = HOME_MATCH_PREFERENCES_WIZARD_CONTENT;

interface IProps {
    actionsAlign?: "center" | "end";
    children?: ReactNode;
    contentKey?: string;
    cover?: ReactNode;
    dataTestId?: string;
    description?: ReactNode;
    footer?: ReactNode;
    isOpen: boolean;
    onAfterClose?: () => void;
    onClose: () => void;
    onSubmit?: (event: FormEvent<HTMLFormElement>) => void;
    progress: number | null;
    progressTestId?: string;
    secondaryAction?: ReactNode;
    shouldCloseOnOverlayClick?: boolean;
    settle?: boolean;
    showIllustration?: boolean;
    title?: ReactNode;
    zIndex?: number;
}

export const UserFlowModal = (props: IProps) => {
    const {
        actionsAlign = "end",
        children,
        contentKey,
        cover,
        dataTestId,
        description,
        footer,
        isOpen,
        onAfterClose,
        onClose,
        onSubmit,
        progress,
        progressTestId,
        secondaryAction,
        settle = false,
        shouldCloseOnOverlayClick = false,
        showIllustration = true,
        title,
        zIndex = zIndexes.landingOverlay
    } = props;
    const {isMobile} = useUserDevice();
    const setFrameRef = useKeyboardFitModalContent(Boolean(isMobile && isOpen));
    const hasIntro = title != null || description != null;
    const introAlign = isMobile ? "left" : "center";
    const progressValue = progress == null ? null : Math.min(100, Math.max(0, progress));
    const stepBody = (
        <>
            {hasIntro && (
                <div className={introStyle}>
                    {title != null && (
                        <Text align={introAlign} as="h2" color="var(--colors-secondary)" preserveFontSize variant="headline_4">
                            {title}
                        </Text>
                    )}
                    {description != null && (
                        <Text align={introAlign} as="div" color="var(--colors-gray-800)" preserveFontSize variant="body_copy_2">
                            {description}
                        </Text>
                    )}
                </div>
            )}
            {children}
        </>
    );
    const frame = (
        <>
            <Modal.TopBar progress={progressValue} progressLabel={PROGRESS} progressTestId={progressTestId} />

            <div className={cx(contentStyle, !contentKey && hasIntro && contentColumnStyle)}>
                {contentKey ? (
                    <UserFlowModalStepContentTransition className={cx(hasIntro && contentColumnStyle)} contentKey={contentKey} settle={settle}>
                        {stepBody}
                    </UserFlowModalStepContentTransition>
                ) : (
                    stepBody
                )}
            </div>

            <div className={cx(footerStyle, actionsAlign === "center" && footerCenterStyle)}>
                {showIllustration && (
                    <span className={illustrationStyle} data-flow-illustration="">
                        <BrandBinocularsIcon height="14.9rem" width="10.3rem" />
                    </span>
                )}
                {(footer != null || secondaryAction != null) && (
                    <Modal.FloatingActions stacked={secondaryAction != null}>
                        {footer}
                        {secondaryAction != null && <div className={secondaryStartStyle}>{secondaryAction}</div>}
                    </Modal.FloatingActions>
                )}
            </div>
        </>
    );

    return (
        <Modal
            className={modalStyle}
            closeButtonClassName={closeButtonStyle}
            dataTestId={dataTestId}
            isOpen={isOpen}
            onAfterClose={onAfterClose}
            onModalClose={onClose}
            shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
            showCloseButton={!cover}
            zIndex={zIndex}
        >
            <Modal.Layout.Empty className={layoutStyle}>
                {onSubmit ? (
                    <form className={stepStyle} onSubmit={onSubmit} ref={setFrameRef}>
                        {frame}
                    </form>
                ) : (
                    <div className={stepStyle} ref={setFrameRef}>
                        {frame}
                    </div>
                )}
                {cover && <div className={coverStyle}>{cover}</div>}
            </Modal.Layout.Empty>
        </Modal>
    );
};

const closeButtonStyle = css({
    top: "2.6rem"
});

const modalStyle = css({
    borderRadius: 0,
    height: "100%",
    inset: 0,
    maxHeight: "100%",
    maxWidth: "100%",
    overflow: "hidden",
    width: "100%",

    md: {
        height: "auto",
        inset: "auto",
        maxHeight: "100vh",
        width: "80rem"
    }
});

const layoutStyle = css({
    backgroundColor: "var(--colors-white)",
    display: "flex",
    flexDirection: "column",
    position: "relative",

    "&&": {
        overflow: "hidden"
    },

    md: {
        backgroundColor: "var(--colors-gray-100)",

        "&&": {
            borderRadius: 2,
            overflow: "hidden"
        }
    }
});

const coverStyle = css({
    alignItems: "center",
    display: "flex",
    inset: 0,
    justifyContent: "center",
    position: "absolute",
    zIndex: "var(--zIndex-raised)"
});

const stepStyle = css({
    display: "flex",
    flexDirection: "column",
    height: "100%",
    maxHeight: "100%",
    minHeight: 0,

    "&:has([data-sms-code]:focus-within) [data-flow-illustration]": {
        transform: "scale(calc(61 / 115))",
        transformOrigin: "left bottom"
    },

    md: {
        height: "auto",
        maxHeight: "100vh",

        "&:has([data-sms-code]:focus-within) [data-flow-illustration]": {
            transform: "none"
        }
    }
});

const contentStyle = css({
    flex: "1 1 auto",
    minHeight: 0,
    overflow: "auto",
    pb: 2,
    pt: 2,
    px: 1.5,

    md: {
        pb: 7.5,
        pt: 3,
        px: 7.5
    }
});

const contentColumnStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 3
});

const introStyle = css({
    display: "flex",
    flexDirection: "column",
    flexShrink: 0,
    gap: 2
});

const footerStyle = css({
    alignItems: "stretch",
    display: "flex",
    flexDirection: "column",
    flexShrink: 0,
    gap: 2,
    height: "auto",
    justifyContent: "flex-end",
    overflow: "visible",
    position: "relative",

    md: {
        alignItems: "center",
        flexDirection: "row",
        height: "9.6rem",
        justifyContent: "flex-end",
        pb: 0,
        pl: 7.5,
        pr: 7.5
    }
});

const secondaryStartStyle = css({
    alignSelf: "center",
    position: "relative",
    zIndex: "var(--zIndex-base)",

    md: {
        alignSelf: "auto",
        mr: "auto",
        order: -1
    }
});

const footerCenterStyle = css({
    md: {
        justifyContent: "center",
        pl: 0,
        pr: 0
    }
});

const illustrationStyle = css({
    bottom: 7,
    left: 1,
    pointerEvents: "none",
    position: "absolute",
    zIndex: 0,

    md: {
        bottom: "-3rem",
        left: "16.2rem",
        pointerEvents: "auto",
        zIndex: "auto"
    }
});
